'use client';

import { useCallback, useState } from 'react';

/**
 * A generic hook for managing item selection
 * @template T The type of the item object
 * @template K The type of the key used to uniquely identify items (default: string)
 */
export default function useSelectItem<T, K = string>(keyExtractor: (item: T) => K) {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const toggleSelection = useCallback((item: T) => {
    const itemKey = keyExtractor(item);

    setSelectedItems((prev) => {
      // Check if the item is already selected by comparing keys
      const isItemSelected = prev.some(selectedItem =>
        keyExtractor(selectedItem) === itemKey,
      );

      if (isItemSelected === true) {
        // Remove the item if already selected
        return prev.filter(selectedItem =>
          keyExtractor(selectedItem) !== itemKey,
        );
      } else {
        // Add the item if not selected
        return [...prev, item];
      }
    });
  }, [keyExtractor]);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const selectItems = useCallback((items: T[]) => {
    setSelectedItems(items);
  }, []);

  const isSelected = useCallback((item: T) => {
    const itemKey = keyExtractor(item);
    return selectedItems.some(selectedItem =>
      keyExtractor(selectedItem) === itemKey,
    );
  }, [selectedItems, keyExtractor]);

  const hasSelection = selectedItems.length > 0;

  return {
    selectedItems,
    toggleSelection,
    clearSelection,
    selectItems,
    isSelected,
    hasSelection,
  };
}
